<template>
  <VaDropdown :offset="[9, 0]" class="profile-dropdown" stick-to-edges :close-on-content-click="false">
    <template #anchor>
      <VaButton preset="secondary" color="textPrimary" class="cursor-pointer">
        <span class="profile-dropdown__anchor">
          <slot />
          <VaAvatar :size="32" :src="avatarUrl || undefined" color="warning">
            <span v-if="!avatarUrl" class="text-sm font-semibold">{{ userInitial }}</span>
          </VaAvatar>
        </span>
      </VaButton>
    </template>
    <VaDropdownContent class="profile-dropdown__content md:w-60 px-0 py-4 w-full">
      <!-- Compte Section -->
      <div class="px-4 py-2">
        <h3 class="uppercase text-[var(--va-secondary)] opacity-80 font-bold text-xs mb-2">Compte</h3>
        <RouterLink :to="{ name: 'preferences' }" class="block dropdown-link">
          <VaButton preset="plain" class="w-full justify-start text-left px-2 py-1 h-auto text-sm menu-button">
            <VaIcon name="mso-account_circle" class="mr-2" color="secondary" />
            <span>Profil</span>
          </VaButton>
        </RouterLink>
        <RouterLink :to="{ name: 'change-password' }" class="block dropdown-link">
          <VaButton preset="plain" class="w-full justify-start text-left px-2 py-1 h-auto text-sm menu-button">
            <VaIcon name="mso-key" class="mr-2" color="secondary" />
            <span>Modifier mot de passe</span>
          </VaButton>
        </RouterLink>
      </div>

      <VaListSeparator class="mx-3 my-2" />

      <!-- Paramètres Section -->
      <div class="px-4 py-2">
        <h3 class="uppercase text-[var(--va-secondary)] opacity-80 font-bold text-xs mb-2">Paramètres</h3>
        <RouterLink :to="{ name: 'settings' }" class="block dropdown-link">
          <VaButton preset="plain" class="w-full justify-start text-left px-2 py-1 h-auto text-sm menu-button">
            <VaIcon name="mso-settings" class="mr-2" color="secondary" />
            <span>Paramètres</span>
          </VaButton>
        </RouterLink>
        <VaButton
          preset="plain"
          class="w-full justify-start text-left px-2 py-1 h-auto text-sm menu-button"
          @click="showHelpModal = true"
        >
          <VaIcon name="mso-help" class="mr-2" color="secondary" />
          <span>Aide et support</span>
        </VaButton>
      </div>

      <VaListSeparator class="mx-3 my-2" />

      <!-- Déconnexion -->
      <div class="px-4 py-2">
        <VaButton
          preset="plain"
          class="w-full justify-start text-left px-2 py-1 h-auto text-sm menu-button-logout"
          @click="handleLogout"
        >
          <VaIcon name="mso-logout" class="mr-2" color="danger" />
          <span>Déconnexion</span>
        </VaButton>
      </div>
    </VaDropdownContent>
  </VaDropdown>

  <!-- Modal Aide et Support -->
  <VaModal v-model="showHelpModal" size="medium" title="Aide et Support" hide-default-actions>
    <div class="help-modal-content">
      <div class="mb-4">
        <h3 class="text-lg font-semibold mb-2">À propos de FinCore One</h3>
        <p class="text-sm text-secondary mb-4">
          FinCore One est une solution complète développée par <strong>PROGRESS SYSTEMS</strong> pour la gestion des
          cabinets d'expertise comptable.
        </p>
      </div>

      <VaListSeparator class="my-4" />

      <div class="mb-4">
        <h3 class="text-lg font-semibold mb-3">Contactez-nous</h3>

        <div class="contact-item mb-3">
          <VaIcon name="phone" class="mr-2" color="primary" />
          <div>
            <div class="text-sm font-semibold">Téléphone</div>
            <div class="text-sm text-secondary">
              <a href="tel:+2250748311349" class="contact-link">07 48 31 13 49</a> /
              <a href="tel:+2250716778913" class="contact-link">07 16 77 89 13</a>
            </div>
          </div>
        </div>

        <div class="contact-item mb-3">
          <VaIcon name="email" class="mr-2" color="primary" />
          <div>
            <div class="text-sm font-semibold">Email</div>
            <div class="text-sm text-secondary">
              <a href="mailto:infos@progress-systems.net" class="contact-link">infos@progress-systems.net</a>
            </div>
          </div>
        </div>

        <div class="contact-item">
          <VaIcon name="business" class="mr-2" color="primary" />
          <div>
            <div class="text-sm font-semibold">Service</div>
            <div class="text-sm text-secondary">Développement - PROGRESS SYSTEMS</div>
          </div>
        </div>
      </div>

      <VaListSeparator class="my-4" />

      <div class="text-center">
        <VaButton color="primary" size="small" @click="downloadGuide">
          <VaIcon name="mso-description" class="mr-2" />
          Guide d'utilisation
        </VaButton>
      </div>
    </div>

    <template #footer>
      <VaButton @click="showHelpModal = false">Fermer</VaButton>
    </template>
  </VaModal>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '../../../../stores/user-store'
import guideFile from '@/components/guide/GUIDE_D_UTILISATION_FINCORE.pdf'

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api'

const router = useRouter()
const store = useUserStore()
const showHelpModal = ref(false)

const userInitial = computed(() => {
  return store.firstName ? store.firstName[0].toUpperCase() : '?'
})

const avatarUrl = computed(() => {
  if (!store.pfp) return undefined
  // Charger directement depuis le backend (pas via proxy Vite)
  return store.pfp.startsWith('http') ? store.pfp : `${apiBaseUrl}${store.pfp}`
})

onMounted(async () => {
  if (!store.firstName) {
    await store.loadUserData()
  }
})

const handleLogout = () => {
  // Supprimer les données de session
  localStorage.removeItem('user')
  sessionStorage.removeItem('sessionActive')

  // Rediriger vers la page de connexion
  router.push({ name: 'login' })
}

const downloadGuide = () => {
  const link = document.createElement('a')
  link.href = guideFile
  link.download = 'GUIDE_D_UTILISATION_FINCORE.pdf'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
</script>

<style lang="scss" scoped>
.profile-dropdown {
  cursor: pointer;

  &__anchor {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__content {
    .va-button {
      color: inherit;
      transition: background-color 0.2s ease;

      &:hover {
        background-color: rgba(0, 0, 0, 0.05);
      }
    }
  }
}

.dropdown-link {
  text-decoration: none;
  display: block;

  .va-button {
    color: var(--va-text-primary);

    &:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }
  }
}

.menu-button {
  color: var(--va-text-primary) !important;
}

.menu-button-logout {
  color: var(--va-danger) !important;
}

.help-modal-content {
  padding: 0.5rem 0;
}

.contact-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.contact-link {
  color: var(--va-primary);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}
</style>
